/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ValidationDialogComponent } from '@Work/Accounts/proprietary/dialogs/validation-dialog/validation-dialog.component';
import { SetCompiledRules, SetEvaluatedStateObjects } from '@Core/models/proprietary/page/page.actions';
import { EventType, PageState } from '@Core/models/proprietary/page/page.state';
import { cloneDeep, isEmptyObject } from 'src/ep-utils';
import { AppService } from '@Core/services/proprietary/app.service';
import {
    CompiledObjectNode,
    CompiledObjectRules,
    CompiledRuleNode,
    EvaluatedObjectRules,
    EvaluatedStateObject,
    FormGroupMapping,
    ObjectRuleSet
} from './object-rule.types';
import { EPStateValidators } from './object-rule.validators';

@Injectable({
    providedIn: 'root'
})
export class ObjectRuleService {
    _lastEvaluatedFormRules: EvaluatedObjectRules;

    get LastEvaluatedFormRules(): EvaluatedObjectRules {
        return this._lastEvaluatedFormRules;
    }

    constructor(private store: Store, private modalService: NgbModal, private app: AppService) { }

    validateState(
        stateEntities: any,
        event: EventType,
        callingOS: any,
        ignoreMissingStateObjects: boolean = false
    ): EvaluatedStateObject[] {
        let compiledRules = this.getCompiledObjectRules();
        let objectValidationEvents = this.store.selectSnapshot(PageState.objectValidationEvents) || [];
        let saveValidationEvent = this.store.selectSnapshot(PageState.saveValidationEvent);
        let evaluatedStateObjects: EvaluatedStateObject[] = [];

        // Update, Rate, and Validate events do not use thier own rules.   
        // They use the current "Save Validation" event (based on the current status of the object)
        if (event === EventType.Update || event === EventType.Rate) {
            // || event === EventType.Validate
            event = saveValidationEvent;
        }

        // If event not in the list of validation events for this object, then trigger error
        if (objectValidationEvents.includes(event) == false) {
            let objectValidationEventList = this.store.selectSnapshot(PageState.objectValidationEventsAsList);
            evaluatedStateObjects.push({
                stateObject: 'Error',
                objectNode: 'Error',
                itemIDField: '',
                objectID: null,
                fieldRuleResults: {},
                error: `Cannot Validate!  Event '${event.toString()}' is not a valid validation event for this object service. Only the following events are allowed: ${objectValidationEventList}`
            });
        }

        for (var objectNode in compiledRules) {
            let stateObject = compiledRules[objectNode].stateObject;
            let itemIDField = compiledRules[objectNode].itemIDField;

            if (typeof stateEntities[stateObject] === 'undefined') {
                if (!ignoreMissingStateObjects && objectNode.startsWith('UD_') == false) {
                    evaluatedStateObjects.push({
                        stateObject: stateObject,
                        objectNode: objectNode,
                        objectID: null,
                        itemIDField: itemIDField,
                        fieldRuleResults: {},
                        error: "Invalid Rule Def: State Entity '" + objectNode + "' not defined for this type object!"
                    });
                }
            } else {
                for (var objID in stateEntities[stateObject]) {
                    let dataNode = stateEntities[stateObject][objID];
                    if (dataNode['nodeType'] && dataNode['nodeType'] != objectNode) {
                        // if this state entity has a nodeType property then only look at ones that match the root objectNode from the ruleDef
                        continue;
                    }
                    if (
                        dataNode.Edit_Status &&
                        (dataNode.Edit_Status === 'D' || dataNode.Edit_Status === 'X' || dataNode.Edit_Status === '?')
                    ) {
                        continue; // If edit status is Deleted or Available(?) then skip validation
                    }

                    let objectValidationResults = this.evaluateObjectRules(
                        dataNode,
                        objectNode,
                        null,
                        event,
                        null,
                        compiledRules,
                        callingOS,
                        true
                    );
                    if (!isEmptyObject(objectValidationResults)) {
                        evaluatedStateObjects.push({
                            stateObject: stateObject,
                            objectNode: objectNode,
                            itemIDField: itemIDField,
                            objectID: objID,
                            fieldRuleResults: objectValidationResults
                        });
                    }
                }
            }
        }
        //console.log(`State Validation Results: ${JSON.stringify(evaluatedStateObjects)}`);

        this.store.dispatch(new SetEvaluatedStateObjects(evaluatedStateObjects, event)); //update evaluated state objects in page state for UX display

        // let msg = `Validated for '${event.toString()}`;

        // evaluatedStateObjects.forEach((object) => {
        //   msg = msg + '\n\n';
        //   let recordKey = object.stateObject + '.$Record';
        //   if (object.fieldRuleResults && object.fieldRuleResults[recordKey]) {
        //     msg = msg + object.fieldRuleResults[recordKey].desc + ' ';
        //   }
        //   msg = msg + '[' + object.objectNode + ' (' + object.objectID + ')]';
        //   if (object.error) {
        //     //critical object definition error
        //     msg = msg + '\n' + object.error;
        //   }
        //   if (object.fieldRuleResults) {
        //     for (var field in object.fieldRuleResults) {
        //       if (object.fieldRuleResults[field].validationRules) {
        //         object.fieldRuleResults[field].validationRules.forEach((error) => {
        //           msg = msg + '\n' + error.message;
        //         });
        //       }
        //     }
        //   }
        // });

        // if (evaluatedStateObjects.length > 0) {
        //   alert(msg);
        // }

        if (evaluatedStateObjects.length > 0) {
            this.modalService.open(ValidationDialogComponent, {
                centered: true,
                size: 'lg'
                //injector: this.injector,
                //windowClass: 'ep-modal-edit-panel'
            });
        }

        return evaluatedStateObjects; //if array length is 0 then there are no errors
    }

    // form - reference to the form being evaluated
    // compiledRules - compiled rules for this type of object service
    // formObject (string or array of strings) - the top level formgroup(s) to have rules evaluated on (e.g. Article, Contract, [Article, BISP_Prod]).  Note: FormObjects in Forms should typically follow the same name as the state object.  Deviations are needed when multiple top lever formgroups are defined
    // objectNodeType (string or array of string) - the nodetype(s) of the respective formgroups -- used to find rules (e.g. CDAA_Vehicle, Contract, [CDAA_Vehicle, Product])
    evaluateFormRules(
        form: UntypedFormGroup,
        callingOS: any,
        formGroupMapping: FormGroupMapping[]
        //formObject: string | string[],
        //objectNodeType: string | string[] = null
    ): EvaluatedObjectRules {
        let dbSectionID = this.store.selectSnapshot(PageState.currentSection)?.DB_Section_ID;
        //var fg: FormGroup;
        var formValidationResults: EvaluatedObjectRules = {};

        let compiledRules: CompiledObjectRules = this.getCompiledObjectRules();
        let event = this.store.selectSnapshot(PageState.anticipatedValidationEvent);

        if (!event && this.app.DesignerMode) {
            event = EventType.Create; // Assume create in desingner mode if anticipated event is not explicitly set
        }

        formGroupMapping.forEach((mapping) => {
            let fg = <UntypedFormGroup>form.get(mapping.formGroup);
            formValidationResults = {
                ...formValidationResults,
                ...this.evaluateObjectRules(
                    fg.getRawValue(),
                    mapping.nodeType,
                    mapping.formGroup,
                    event,
                    dbSectionID,
                    compiledRules,
                    callingOS
                )
            };
        });

        if (this.app.Settings.Debug.ShowRuleService) {
            // only update last evaluated form if rule debugger on
            this._lastEvaluatedFormRules = formValidationResults;
        } else {
            this._lastEvaluatedFormRules = null;
        }
        return formValidationResults;
    }

    private evaluateObjectRules(
        dataNode: any,
        objectNodeType: string,
        resultObject: string, // for stateValidation pass null,  for form validation pass the formGroupName
        event: EventType,
        dbSectionID: number,
        compiledRules: CompiledObjectRules,
        callingOS: any,
        stateValidationMode: boolean = false
    ): EvaluatedObjectRules {
        let compiledObjectRules = compiledRules[objectNodeType];
        let objectValidationResults: EvaluatedObjectRules = {};

        if (!compiledObjectRules) {
            //console.warn(`Warning: No rule definitions found for objectNodeType: "${objectNodeType}"`);
            return objectValidationResults;
        }

        let stateObject = compiledObjectRules.stateObject;
        if (!resultObject) {
            resultObject = stateObject;
        }
        let parentNodes = {}; // many need to optimize the parent node stuff so we don't re-lookup on every record.
        let parms = {};
        let errorCount = 0;

        let eventStr = '';
        if (event) {
            eventStr = event.toString().toLowerCase();
        } else {
            return;
        }

        //console.log(`Evaluating object rules for ${stateObject}.  Event=${eventStr}`);

        // Evaluate Parameter Values
        for (var parm in compiledObjectRules.parms) {
            let parmValue: string = '';
            let parmFieldName = compiledObjectRules.parms[parm].fieldName;
            let parmStateObject = compiledObjectRules.parms[parm].stateObject;
            switch (parmStateObject) {
                case 'Self':
                    break;
                case 'This':
                    parmValue = this.getObjectDataValue(dataNode, parmFieldName); //TODO -- need to revise to handle multi depth fields  Bill_To.Entity
                    break;
                case 'GlobalOption':
                    parmValue = this.app.getGlobalOptionValue(parmFieldName);
                    break;
                case 'BookOption':
                    parmValue = this.app.getBookOptionValue(parmFieldName);
                    break;
                case 'UserOption':
                    parmValue = this.app.getUserOptionValue(parmFieldName);
                    break;
                case 'RoleOption':
                    parmValue = this.app.getRoleOptionValue(parmFieldName);
                    break;
                case 'SecurityOption':
                    parmValue = this.app.getSecurityOptionValue(parmFieldName);
                    break;
                case 'ControlledData':
                    let tableName = parmFieldName.split('|')[0];
                    let columnName = parmFieldName.split('|')[1];

                    parmValue = this.app.hasControlledDataRights(tableName, columnName);
                    break;
                case 'System':
                    switch (parmFieldName) {
                        case 'Book_Entity_Name':
                            parmValue = this.app.dsSystemInfo.objectNode.Context.Book.Entity_Name;
                            break;
                        case 'Book_Entity_ID':
                            parmValue = this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID;
                            break;
                        case 'IsSaaS':
                            parmValue = 'Y';
                            break;
                    }

                    break;
                default:
                    //TODO: lookup data for parent component in state object
                    //       Need to make this efficient.  Don't lookup parent for each field  -- remember if the record has been looked up
                    if (!parentNodes[parmStateObject]) {
                        parentNodes[parmStateObject] = {};
                        if (!callingOS.getAncestorNode) {
                            parentNodes[parmStateObject] = {
                                Ancestor_Error: `Object Service does not support ancestor based rules (no getAncestorNode function)`
                            };
                        } else {
                            parentNodes[parmStateObject] = callingOS.getAncestorNode(parmStateObject, stateObject, dataNode); // this should be set to the parent node in state that we lookup
                        }
                    }
                    parmValue = this.getObjectDataValue(parentNodes[parmStateObject], parmFieldName); //TODO -- need to revise to handle multi depth fields  Bill_To.Entity
            }
            parms[parm] = typeof parmValue === 'string' ? parmValue.toUpperCase() : parmValue; // uppercase data for comparisons. -- do this in getValue?   Make sure the uppercase literal values in thier rules.
        }

        let recordKey = resultObject + '.$Record';
        // populate name and desc on "$Record" node

        if (compiledObjectRules.fields.$Record) {
            if (!objectValidationResults[recordKey]) {
                objectValidationResults[recordKey] = { desc: '' };
            }
            // if (compiledObjectRules.fields.$Record.name) {
            //   objectValidationResults[recordKey].name = compiledObjectRules.fields.$Record.name;
            // }
            if (compiledObjectRules.fields.$Record.desc) {
                let recordDesc = compiledObjectRules.fields.$Record.desc;
                if (recordDesc.includes('parm[')) {
                    // if the description contains a parameter then we have to call a function to calculate.  otherwise just use the literal message.
                    try {
                        let fnRecordMsg = new Function('parm', 'return(' + recordDesc + ')');
                        recordDesc = fnRecordMsg(parms);
                    } catch (error) {
                        recordDesc = 'Invalid $Record desc expression syntax';
                        console.error(
                            `Invalid $Record desc expression syntax (${compiledObjectRules.fields.$Record.desc})! [stateObject=${stateObject}  field=${recordKey}]    See error detail below:`
                        );
                        console.error(error);
                    }
                }
                if (recordDesc > '') {
                    objectValidationResults[recordKey].desc = recordDesc;
                }
            }
        }

        // Evaluate Validation Rules
        for (var field in compiledObjectRules.validationRules) {
            let validators: CompiledRuleNode[] = compiledObjectRules['validationRules'][field];
            let fieldPath = field;

            // if "field" starts with ep then it is field group, field section, or field block, otherwise it is a datafield that needs to be prefixed with state object
            if (!field.startsWith('ep')) {
                fieldPath = resultObject + '.' + fieldPath;
            }

            let fieldDesc = field.replace(/_/g, ' '); //replace _ with space
            if (fieldDesc.startsWith('UD Data.')) {
                // Remove UD_Data prefix and then uppercase the first letter of each word.
                fieldDesc = fieldDesc.replace('UD Data.', '').replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
            }
            if (field == '$Record') {
                if (compiledObjectRules.fields && compiledObjectRules.fields[field] && compiledObjectRules.fields[field].name) {
                    fieldDesc = compiledObjectRules.fields[field].name;
                }
            } else {
                if (compiledObjectRules.fields && compiledObjectRules.fields[field] && compiledObjectRules.fields[field].desc) {
                    fieldDesc = compiledObjectRules.fields[field].desc;
                }
            }

            let fieldValue = this.getObjectDataValue(dataNode, field);
            parms['Self'] = typeof fieldValue === 'string' ? fieldValue.toUpperCase() : fieldValue; //add this field to the parm array as 'self'  // should self be compiled to its proper This.Field_Name
            // TODO: if required rule and value is empty (meaning required failed), should we skip other rules

            let requiredIfCount = 0;
            let requiredIfErrorCount = 0;
            let validIfCount = 0;
            let validIfErrorCount = 0;
            let requiredIfErrorMsg = '';
            let validIfErrorMsg = '';

            validators.forEach((ruleNode, index) => {
                //Don't evalate rules that do not reference the current event
                if (ruleNode.events && ruleNode.events > '' && ruleNode.events.includes(eventStr) == false) {
                    return;
                }

                let rule = ruleNode.rule;
                let ruleName = ruleNode.rule.split('(')[0].trim();

                switch (ruleName) {
                    case 'required':
                        if (EPStateValidators.required(fieldValue) == false) {
                            errorCount++;
                            if (!objectValidationResults[fieldPath]) {
                                objectValidationResults[fieldPath] = { desc: fieldDesc };
                            }
                            if (!objectValidationResults[fieldPath].validationRules) {
                                objectValidationResults[fieldPath].validationRules = [];
                            }

                            //future debug mode: add source and sourceRuleNode below
                            objectValidationResults[fieldPath].validationRules.push(
                                stateValidationMode == true
                                    ? {
                                        rule: ruleName,
                                        message: fieldDesc + ' is required.'
                                    }
                                    : {
                                        rule: ruleName
                                    }
                            );
                        }
                        break;

                    case 'requiredIf':
                        try {
                            requiredIfCount++;

                            let requiredCondition = rule.replace('requiredIf(', 'return(').trim();
                            let fnRequiredCondition = new Function('parm', requiredCondition);

                            if (EPStateValidators.requiredIf(fieldValue, fnRequiredCondition(parms)) == false) {
                                errorCount++;
                                requiredIfErrorCount++;
                                let thisRequiredIfMessage = '';
                                if (ruleNode.message) {
                                    thisRequiredIfMessage = ruleNode.message;
                                    if (thisRequiredIfMessage.includes('parm[')) {
                                        // if the message contains a parameter then we have to call a function to calculate.  otherwise just use the literal message.
                                        let fnConditionMsg = new Function('parm', 'return(' + thisRequiredIfMessage + ')');
                                        thisRequiredIfMessage = fnConditionMsg(parms);
                                    }
                                }
                                if (thisRequiredIfMessage > '') {
                                    requiredIfErrorMsg = `${requiredIfErrorMsg} ${thisRequiredIfMessage}. `;
                                }
                            }
                        } catch (error) {
                            errorCount++;
                            requiredIfErrorCount++;
                            requiredIfErrorMsg = `${requiredIfErrorMsg} Invalid rule syntax(${rule}). `;
                            console.error(
                                `Invalid requiredIf rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                            );
                            console.error(error);
                        }
                        break;

                    case 'validIf':
                        try {
                            validIfCount++;

                            if (field == '$Record' || fieldValue > '') {
                                // validation rules are only evaluated if there is a value for the field.   To require a value, set a required rule.   $Record wide validation rules are always evaluated.
                                let validCondition = rule.replace('validIf(', 'return(').trim();
                                let fnValidCondition = new Function('parm', validCondition);

                                if (EPStateValidators.validIf(fnValidCondition(parms)) == false) {
                                    validIfErrorCount++;
                                    errorCount++;
                                    let thisValidIfMessage = '';
                                    if (ruleNode.message) {
                                        thisValidIfMessage = ruleNode.message;
                                        if (thisValidIfMessage.includes('parm[')) {
                                            // if the message contains a parameter then we have to call a function to calculate.  otherwise just use the literal message.
                                            let fnConditionMsg = new Function('parm', 'return(' + thisValidIfMessage + ')');
                                            thisValidIfMessage = fnConditionMsg(parms);
                                        }
                                    }
                                    if (thisValidIfMessage > '') {
                                        validIfErrorMsg = `${validIfErrorMsg} ${thisValidIfMessage}. `;
                                    }
                                }
                            }
                        } catch (error) {
                            validIfErrorCount++;
                            errorCount++;
                            validIfErrorMsg = `${validIfErrorMsg} Invalid rule syntax(${rule}). `;
                            console.error(
                                `Invalid validIf rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                            );
                            console.error(error);
                        }
                        break;

                    case 'minLength':
                        let min = parseInt(rule.replace('minLength(', '').replace(')', ''), 10);
                        if (EPStateValidators.minLength(fieldValue, min) == false) {
                            errorCount++;
                            if (!objectValidationResults[fieldPath]) {
                                objectValidationResults[fieldPath] = { desc: fieldDesc };
                            }
                            if (!objectValidationResults[fieldPath].validationRules) {
                                objectValidationResults[fieldPath].validationRules = [];
                            }

                            //future debug mode: add source and sourceRuleNode below
                            objectValidationResults[fieldPath].validationRules.push(
                                stateValidationMode == true
                                    ? {
                                        rule: rule,
                                        message: fieldDesc + ' is too short. A minimum of ' + min + ' character(s) required.'
                                    }
                                    : {
                                        rule: rule
                                    }
                            );
                        }
                        break;

                    case 'maxLength':
                        let max = parseInt(rule.replace('maxLength(', '').replace(')', ''), 10);
                        if (EPStateValidators.maxLength(fieldValue, max) == false) {
                            errorCount++;
                            if (!objectValidationResults[fieldPath]) {
                                objectValidationResults[fieldPath] = { desc: fieldDesc };
                            }
                            if (!objectValidationResults[fieldPath].validationRules) {
                                objectValidationResults[fieldPath].validationRules = [];
                            }

                            //future debug mode: add source and sourceRuleNode below
                            objectValidationResults[fieldPath].validationRules.push(
                                stateValidationMode == true
                                    ? {
                                        rule: rule,
                                        message: fieldDesc + ' is too long. A Maximum of ' + max + ' character(s) is allowed.'
                                    }
                                    : {
                                        rule: rule
                                    }
                            );
                        }
                        break;

                    case 'range':
                        // range(0,9)
                        let rangeMin = null;
                        let rangeMax = null;
                        let rangeParms = rule.replace(/ /g, '').toLowerCase().replace('range(', '').replace(')', '').split(',');

                        if (rangeParms.length > 0) {
                            rangeMin = parseInt(rangeParms[0], 10);
                        }
                        if (rangeParms.length > 1) {
                            rangeMax = parseInt(rangeParms[1], 10);
                        }

                        if (EPStateValidators.range(fieldValue, rangeMin, rangeMax) == false) {
                            errorCount++;
                            if (!objectValidationResults[fieldPath]) {
                                objectValidationResults[fieldPath] = { desc: fieldDesc };
                            }
                            if (!objectValidationResults[fieldPath].validationRules) {
                                objectValidationResults[fieldPath].validationRules = [];
                            }

                            //future debug mode: add source and sourceRuleNode below
                            objectValidationResults[fieldPath].validationRules.push(
                                stateValidationMode == true
                                    ? {
                                        rule: rule,
                                        message: fieldDesc + ' must be between ' + rangeMin + ' and ' + rangeMax + '.'
                                    }
                                    : {
                                        rule: rule
                                    }
                            );
                        }
                        break;

                    default:
                        errorCount++;
                        if (!objectValidationResults[fieldPath]) {
                            objectValidationResults[fieldPath] = { desc: fieldDesc };
                        }
                        if (!objectValidationResults[fieldPath].validationRules) {
                            objectValidationResults[fieldPath].validationRules = [];
                        }

                        //future debug mode: add source and sourceRuleNode below
                        objectValidationResults[fieldPath].validationRules.push(
                            stateValidationMode == true
                                ? {
                                    rule: rule,
                                    message: "'Rule '" + ruleName + "' on " + fieldDesc + ' is unknown.'
                                }
                                : {
                                    rule: rule
                                }
                        );
                }
            });

            if (stateValidationMode ? requiredIfErrorCount > 0 : requiredIfCount > 0) {
                if (!objectValidationResults[fieldPath]) {
                    objectValidationResults[fieldPath] = { desc: fieldDesc };
                }
                if (!objectValidationResults[fieldPath].validationRules) {
                    objectValidationResults[fieldPath].validationRules = [];
                }

                if (!stateValidationMode && requiredIfErrorMsg == '' && requiredIfErrorCount > 0) {
                    requiredIfErrorMsg = 'NO-MESSAGE';
                }

                requiredIfErrorMsg = requiredIfErrorMsg.trim();

                //future debug mode: add source and sourceRuleNode below
                objectValidationResults[fieldPath].validationRules.push(
                    stateValidationMode == true
                        ? {
                            rule: `requiredIf(${requiredIfErrorMsg})`,
                            message:
                                requiredIfErrorMsg == 'NO-MESSAGE' ? `${fieldDesc} is conditionally required.` : requiredIfErrorMsg
                        }
                        : {
                            rule: `requiredIf(${requiredIfErrorMsg})`
                        }
                );
            }

            if (stateValidationMode ? validIfErrorCount > 0 : validIfCount > 0) {
                if (!objectValidationResults[fieldPath]) {
                    objectValidationResults[fieldPath] = { desc: fieldDesc };
                }
                if (!objectValidationResults[fieldPath].validationRules) {
                    objectValidationResults[fieldPath].validationRules = [];
                }

                if (!stateValidationMode && validIfErrorMsg == '' && validIfErrorCount > 0) {
                    validIfErrorMsg = 'NO-MESSAGE';
                }

                validIfErrorMsg = validIfErrorMsg.trim();

                //future debug mode: add source and sourceRuleNode below
                objectValidationResults[fieldPath].validationRules.push(
                    stateValidationMode == true
                        ? {
                            rule: `validIf(${validIfErrorMsg})`, //could we pass a reference to the evaluated rules array instead?
                            message: validIfErrorMsg == 'NO-MESSAGE' ? `${fieldDesc} is conditionally invalid.` : validIfErrorMsg
                        }
                        : {
                            rule: `validIf(${validIfErrorMsg})`
                        }
                );
            }
        }

        if (stateValidationMode == true && errorCount == 0) {
            // console.warn(
            //   `Delete validation record with no errors OR use a local var and only add when needed:  ${recordKey}`
            // );
            delete objectValidationResults[recordKey]; // remove nodes without errors
        }

        ////////////// ONLY RUN FOR FORM EVALUATION (NOT RUN FOR STATE VALIDATION) //////////////////////
        if (stateValidationMode == false) {
            // Evaluate DISABLE Rules
            for (var field in compiledObjectRules.disableRules) {
                let fieldRules: CompiledRuleNode[] = compiledObjectRules.disableRules[field];
                let fieldPath = field;
                // if "field" starts with ep then it is field group, field section, or field block, otherwise it is a datafield that needs to be prefixed with state object
                if (!field.startsWith('ep')) {
                    fieldPath = resultObject + '.' + fieldPath;
                }

                //TODO: optimize -- only lookup field desc when building an error.   creat an addError function????
                let fieldDesc = field; // add code later to strip out '.' and '_'
                if (compiledObjectRules.fields && compiledObjectRules.fields[field] && compiledObjectRules.fields[field]) {
                    fieldDesc = compiledObjectRules.fields[field].desc;
                }

                let fieldValue = this.getObjectDataValue(dataNode, field);
                parms['Self'] = typeof fieldValue === 'string' ? fieldValue.toUpperCase() : fieldValue; //add this field to the parm array as 'self'  // should self be compiled to its proper This.Field_Name

                // TODO: if required rule and value is empty (meaning required failed), should we skip other rules

                let disableRuleCount = 0;
                let disableIfCount = 0;
                let disableIfMsg = '';
                let disableField = false;

                fieldRules.forEach((ruleNode, index) => {
                    //console.log(fieldDesc + '>>> ' + rule);
                    let rule = ruleNode.rule;
                    let ruleName = ruleNode.rule.split('(')[0].trim();
                    switch (ruleName) {
                        case 'disable':
                            disableRuleCount++;
                            if (!ruleNode.dbSectionID || ruleNode.dbSectionID == dbSectionID) {
                                disableField = true;
                            }
                            break;

                        case 'disableIf':
                            try {
                                disableRuleCount++;
                                disableIfCount++;
                                let disabledCondition = rule.replace('disableIf(', 'return(').trim();
                                let fnDisabledCondition = new Function('parm', disabledCondition);

                                if (
                                    fnDisabledCondition(parms) == true &&
                                    (!ruleNode.dbSectionID || ruleNode.dbSectionID == dbSectionID)
                                ) {
                                    disableField = true;
                                    let thisDisableIfMessage = '';
                                    if (ruleNode.message) {
                                        thisDisableIfMessage = ruleNode.message;
                                        if (thisDisableIfMessage.includes('parm[')) {
                                            // if the message contains a parameter then we have to call a function to calculate.  otherwise just use the literal message.
                                            let fnConditionMsg = new Function('parm', 'return(' + thisDisableIfMessage + ')');
                                            thisDisableIfMessage = fnConditionMsg(parms);
                                        }
                                    }
                                    if (thisDisableIfMessage > '') {
                                        disableIfMsg = `${disableIfMsg} ${thisDisableIfMessage}. `;
                                    }
                                }
                            } catch (error) {
                                disableField = true;
                                disableIfMsg = `${disableIfMsg}  Invalid rule syntax(${rule}). `;

                                console.error(
                                    `Invalid disableIf rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                                );
                                console.error(error);
                            }
                            break;

                        default:
                        //TODO: Trigger some type of warning or error for invalid disable rules
                    }
                });

                if (disableRuleCount > 0) {
                    if (!objectValidationResults[fieldPath]) {
                        objectValidationResults[fieldPath] = { desc: fieldDesc };
                    }
                    if (disableIfCount > 0 && disableIfMsg == '') {
                        disableIfMsg = 'Field is conditionally disabled.';
                    }
                    disableIfMsg = disableIfMsg.trim();
                    objectValidationResults[fieldPath].disable = disableField;
                    objectValidationResults[fieldPath].disableMessage = disableField
                        ? `Field Disabled.${disableIfMsg > '' ? ' ' + disableIfMsg : disableIfMsg}`
                        : '';
                }
            }

            // Evaluate HIDE Rules
            for (var field in compiledObjectRules.hideRules) {
                let fieldRules: CompiledRuleNode[] = compiledObjectRules.hideRules[field];
                let fieldPath = field;
                // if "field" starts with ep then it is field group, field section, or field block, otherwise it is a datafield that needs to be prefixed with state object
                if (!field.startsWith('ep')) {
                    fieldPath = resultObject + '.' + fieldPath;
                }

                //TODO: optimize -- only lookup field desc when building an error.   creat an addError function????
                let fieldDesc = field; // add code later to strip out '.' and '_'
                if (compiledObjectRules.fields && compiledObjectRules.fields[field] && compiledObjectRules.fields[field]) {
                    fieldDesc = compiledObjectRules.fields[field].desc;
                }

                let fieldValue = this.getObjectDataValue(dataNode, field);
                parms['Self'] = typeof fieldValue === 'string' ? fieldValue.toUpperCase() : fieldValue; //add this field to the parm array as 'self'  // should self be compiled to its proper This.Field_Name

                // TODO: if required rule and value is empty (meaning required failed), should we skip other rules

                let hideRuleCount = 0;
                let hideIfCount = 0;
                let hideIfMsg = '';
                let hideField = false;

                fieldRules.forEach((ruleNode, index) => {
                    //console.log(fieldDesc + '>>> ' + rule);
                    let rule = ruleNode.rule;
                    let ruleName = ruleNode.rule.split('(')[0].trim();
                    switch (ruleName) {
                        case 'hide':
                            hideRuleCount++;
                            if (!ruleNode.dbSectionID || ruleNode.dbSectionID == dbSectionID) {
                                hideField = true;
                            }
                            break;

                        case 'hideIf':
                            try {
                                hideRuleCount++;
                                let hideCondition = rule.replace('hideIf(', 'return(').trim();
                                let fnHideCondition = new Function('parm', hideCondition);

                                if (
                                    fnHideCondition(parms) == true &&
                                    (!ruleNode.dbSectionID || ruleNode.dbSectionID == dbSectionID) &&
                                    !this.app.DesignerMode
                                ) {
                                    //don't evaluate hideIf if in UX Designer Mode
                                    hideIfCount++;
                                    hideField = true;
                                    let thisHideIfMessage = '';
                                    if (ruleNode.message) {
                                        thisHideIfMessage = ruleNode.message;
                                        if (thisHideIfMessage.includes('parm[')) {
                                            // if the message contains a parameter then we have to call a function to calculate.  otherwise just use the literal message.
                                            let fnConditionMsg = new Function('parm', 'return(' + thisHideIfMessage + ')');
                                            thisHideIfMessage = fnConditionMsg(parms);
                                        }
                                    }
                                    if (thisHideIfMessage > '') {
                                        hideIfMsg = `${hideIfMsg} ${thisHideIfMessage}. `;
                                    }
                                }
                            } catch (error) {
                                hideIfCount++;
                                hideField = true;
                                hideIfMsg = `${hideIfMsg} Invalid rule syntax(${rule}). `;
                                console.error(
                                    `Invalid hideIf rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                                );
                                console.error(error);
                            }
                            break;

                        default:
                        //TODO: Trigger some type of warning or error for invalid hide rules
                    }
                });

                if (hideRuleCount > 0) {
                    if (!objectValidationResults[fieldPath]) {
                        objectValidationResults[fieldPath] = { desc: fieldDesc };
                    }
                    if (hideIfCount > 0 && hideIfMsg == '') {
                        hideIfMsg = 'Field is conditionally hidden.';
                    }
                    hideIfMsg = hideIfMsg.trim();
                    objectValidationResults[fieldPath].hide = hideField;
                    objectValidationResults[fieldPath].hideMessage = hideField
                        ? `Field Hidden.${hideIfMsg > '' ? ' ' + hideIfMsg : hideIfMsg}`
                        : '';
                }
            }

            // Evaluate PROPERTY Rules
            for (var field in compiledObjectRules.propertyRules) {
                let fieldRules: CompiledRuleNode[] = compiledObjectRules.propertyRules[field];
                let fieldPath = field;
                // if "field" starts with ep then it is field group, field section, or field block, otherwise it is a datafield that needs to be prefixed with state object
                if (!field.startsWith('ep')) {
                    fieldPath = resultObject + '.' + fieldPath;
                }

                //TODO: optimize -- only lookup field desc when building an error.   creat an addError function????
                let fieldDesc = field; // add code later to strip out '.' and '_'
                if (compiledObjectRules.fields && compiledObjectRules.fields[field] && compiledObjectRules.fields[field]) {
                    fieldDesc = compiledObjectRules.fields[field].desc;
                }

                let fieldValue = this.getObjectDataValue(dataNode, field);
                parms['Self'] = typeof fieldValue === 'string' ? fieldValue.toUpperCase() : fieldValue; //add this field to the parm array as 'self'  // should self be compiled to its proper This.Field_Name

                // TODO: if required rule and value is empty (meaning required failed), should we skip other rules

                let propertyRuleCount = 0;
                let label: string = null;
                let columnSpan: string = null;

                fieldRules.forEach((ruleNode, index) => {
                    //console.log(fieldDesc + '>>> ' + rule);
                    let rule = ruleNode.rule;
                    let ruleName = ruleNode.rule.split('(')[0].trim();
                    let propertyName = ruleName.replace('property.', '');
                    let propertyValue: string = null;
                    let propertyMsg = '';
                    propertyRuleCount++;

                    if (!ruleNode.dbSectionID || ruleNode.dbSectionID == dbSectionID) {
                        if (rule.includes('dynamicLabel(')) {
                            try {
                                let propertyFormula = rule.replace(ruleName + '(', '').trim();
                                propertyFormula = propertyFormula.slice(0, -1); // Remove the ending ")" as well
                                let dynamicLabelFormula = propertyFormula.replace('dynamicLabel(', '').trim();
                                dynamicLabelFormula = dynamicLabelFormula.slice(0, -1); // Remove the ending ")" as well

                                // Replace ' with ""
                                dynamicLabelFormula = dynamicLabelFormula.replace(/'/gi, '"');

                                let fnProperyValue = new Function('parm', dynamicLabelFormula);

                                propertyValue = fnProperyValue(parms);
                            } catch (error) {
                                propertyMsg = `${propertyMsg} Invalid rule syntax(${rule}). `;
                                console.error(
                                    `Invalid property rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                                );
                                console.error(error);
                            }
                        } else if (rule.includes('parm[')) {
                            try {
                                let propertyFormula = rule.replace(ruleName + '(', 'return(').trim();
                                let fnProperyValue = new Function('parm', propertyFormula);
                                propertyValue = fnProperyValue(parms);
                            } catch (error) {
                                propertyMsg = `${propertyMsg} Invalid rule syntax(${rule}). `;
                                console.error(
                                    `Invalid property rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   See error detail below:`
                                );
                                console.error(error);
                            }
                        } else {
                            // a literal property value without parameters
                            propertyValue = rule.substring(ruleName.length + 2, rule.length - 2); // remove the rulename plus opening paren and quote  AND  closing quote and  paren
                        }

                        switch (propertyName) {
                            case 'label':
                                label = propertyValue || label;
                                break;
                            case 'columnSpan':
                                columnSpan = propertyValue || columnSpan;
                                break;
                            default:
                                propertyMsg = `${propertyMsg} Invalid rule syntax(${rule}). "${propertyName}" is not a supported property. `;
                                console.error(
                                    `Invalid property rule expression syntax (${rule})!  [stateObject=${stateObject}  field=${fieldPath}]   "${propertyName}" is not a supported property.`
                                );
                        }
                    }
                });

                if (propertyRuleCount > 0) {
                    if (!objectValidationResults[fieldPath]) {
                        objectValidationResults[fieldPath] = { desc: fieldDesc };
                    }

                    if (label != null) objectValidationResults[fieldPath].label = label;
                }

                if (columnSpan != null) objectValidationResults[fieldPath].columnSpan = columnSpan;
            }
        }

        return objectValidationResults;
    }

    private getObjectDataValue(obj: any, propertyPath: string) {
        // get value for any depth path in dot notation.     'Book_Cat_A'  'Bill_To_Entity.Entity_ID'
        let prop = propertyPath.split('.');
        let empty = '';
        let curObj = obj;
        for (var i = 0; i < prop.length; i++) {
            if (typeof curObj[prop[i]] === 'undefined') {
                return empty;
            }
            curObj = curObj[prop[i]];
        }
        return curObj;
    }

    //If one or more rule sets has been added since the last rule compile then
    //compile the new set(s) and append to already compiled rules.
    private getCompiledObjectRules(): CompiledObjectRules {
        let compileNeeded = this.store.selectSnapshot(PageState.objectRuleCompileDirty);
        let compiledObjectRules = this.store.selectSnapshot(PageState.compiledObjectRules);
        let objectValidationEventList = this.store.selectSnapshot(PageState.objectValidationEventsAsList) || '';
        if (compileNeeded) {
            let objectRuleSets = this.store.selectSnapshot(PageState.objectRuleSets);

            objectRuleSets.forEach((set) => {
                if (!set.compiled) {
                    compiledObjectRules = this.compileRules(compiledObjectRules, set.ruleSet, objectValidationEventList);
                }
            });

            this.store.dispatch(new SetCompiledRules(compiledObjectRules));

            //console.log(`Compiled Rule: ${JSON.stringify(compiledObjectRules)}`);
        }
        return compiledObjectRules;
    }

    //TODO - Make Private???
    compileRules(
        existingCompiledRules: CompiledObjectRules,
        ruleDef: ObjectRuleSet,
        objectValidationEventList: string
    ): any {
        let compiledRules: CompiledObjectRules = cloneDeep(existingCompiledRules);
        let source = ruleDef.source;
        let defaultEvents = ';';
        if (ruleDef.defaultEvents) {
            defaultEvents = ruleDef.defaultEvents.toLowerCase().trim() + ';';
        }
        if (defaultEvents == ';') {
            defaultEvents = 'all;';
        } //empty defaults is interpreted as all
        if (defaultEvents.includes('all;')) {
            // if 'all' events tag is included then replace it with the list of validation events from the object definition
            defaultEvents = defaultEvents.replace('all;', objectValidationEventList.toLowerCase());
        }

        //TODO:  add code to validate that the events in the list exist in the objectValidationEventList?

        //TODO:  implement (-)Event syntax for defaults?   eg: All;-Delete

        //console.log(`Compile Rules>>> Source: ${source}    [default events: ${defaultEvents}]`);

        for (var objectNode in ruleDef.objects) {
            let stateObject = ruleDef.objects[objectNode].stateObject;
            let itemIDField = ruleDef.objects[objectNode].itemIDField;
            if (!compiledRules[objectNode]) {
                let newObjectRuleDef: CompiledObjectNode = {
                    stateObject: stateObject,
                    itemIDField: itemIDField,
                    parms: {},
                    fields: {},
                    validationRules: {},
                    disableRules: {},
                    hideRules: {},
                    propertyRules: {}
                };
                compiledRules[objectNode] = newObjectRuleDef;
            }

            if (source.startsWith('Core')) {
                compiledRules[objectNode].stateObject = stateObject;
                compiledRules[objectNode].itemIDField = itemIDField;
            }

            //loop through fields
            for (var field in ruleDef.objects[objectNode].fields) {
                //console.log(`processing field: ${field}`)
                //populate field def
                if (!compiledRules[objectNode].fields[field]) {
                    compiledRules[objectNode].fields[field] = { desc: ruleDef.objects[objectNode].fields[field].desc };
                }

                if (field == '$Record') {
                    //if this is the record node then populate the name and compile the desc (since $Record node can have a computed desc)
                    //compiledRules[objectNode].fields[field].name = ruleDef.objects[objectNode].fields[field].name;
                    let desc = this.compileRule(
                        objectNode,
                        ruleDef.objects[objectNode].fields[field].desc,
                        compiledRules,
                        ruleDef.objects[objectNode].variables
                    );
                    if (desc && desc > '') {
                        compiledRules[objectNode].fields[field].desc = desc;
                    }
                }

                //loop through rules for field
                for (var rule in ruleDef.objects[objectNode].fields[field].rules) {
                    let ruleNode = ruleDef.objects[objectNode].fields[field].rules[rule];

                    //////////// Put effective event list on this rule /////////////////////////////

                    let dbSectionID = null;
                    let events = defaultEvents;
                    let ruleNodeEvents = (ruleNode.events || '').toLowerCase() + ';';

                    if (ruleNodeEvents.length > 1) {
                        if (ruleNodeEvents.indexOf('-') == -1 && ruleNodeEvents.indexOf('+') == -1) {
                            //if no + or - prefix on rule events then override default rules completely (event='Accept;Reject')
                            events = ruleNodeEvents;
                            if (events.includes('all;')) {
                                events = events.replace('all;', objectValidationEventList.toLowerCase());
                            }
                        } else {
                            //else add/remove events specified directly on rule  (event='+Submit;-Reject')
                            if (ruleNodeEvents.includes('all;')) {
                                // if all or +all included then include set the default to all validation events
                                events = objectValidationEventList.toLowerCase();
                            }
                            //loop through + or - events and add or remove
                            ruleNodeEvents.split(';').forEach((event) => {
                                if (event > '' && !event.endsWith('all')) {
                                    if (event.startsWith('+')) {
                                        //add event to default events -- if default excluded this event then remove the exclusion event
                                        event = event.substr(1); //strip off +
                                        events = events.replace('-' + event + ';', ''); //remove -event if exists
                                        events = events + event + ';';
                                    } else if (event.startsWith('-')) {
                                        //remove event from default events -- if default included this event then remove the event
                                        event = event.substr(1); //strip off -
                                        events = events.replace(event + ';', ''); //remove event if exists
                                    } else {
                                        console.error(
                                            `Invalid event syntax on rule.  Missing + or - prefix on one or more events.  Events=${ruleNodeEvents}`
                                        );
                                    }
                                }
                            });
                        }
                    }

                    ////////////////////////////////////////////////////////////////////////////
                    // Populate dbSectionID on visual rules (disable/hide) for non-base rule sets
                    // RuleSet source format is Config.XxxxxxEditPanelComponent.NNNNNN where NNNN is the DBSectionID for the section
                    if (source.startsWith('Config.')) {
                        dbSectionID = source.split('.')[2]; //format of user rules is Config.XxxxxxEditPanelComponent.NNNNNN where NNNN is the DBSectionID for the section
                    }

                    ////////////////////////////////////////////////////////////////////////////

                    let compiledRuleNode: CompiledRuleNode = {
                        rule: this.compileRule(objectNode, ruleNode.rule, compiledRules, ruleDef.objects[objectNode].variables),
                        message: this.compileRule(
                            objectNode,
                            ruleNode.message,
                            compiledRules,
                            ruleDef.objects[objectNode].variables
                        ),
                        events: events,
                        dbSectionID: dbSectionID
                        //source: source,     -- later debug mode to link back to source ruler set
                        //sourceRuleNode: ruleNode   -- later debug mode to link back to source rule
                        //function: ???? -- compile dynamic functions now?
                    };

                    if (ruleNode.rule.startsWith('disable')) {
                        //add disable rule
                        if (!compiledRules[objectNode].disableRules[field]) {
                            compiledRules[objectNode].disableRules[field] = [];
                        }
                        compiledRules[objectNode].disableRules[field].push(compiledRuleNode);
                    } else if (ruleNode.rule.startsWith('hide')) {
                        //add hide rule
                        if (!compiledRules[objectNode].hideRules[field]) {
                            compiledRules[objectNode].hideRules[field] = [];
                        }
                        compiledRules[objectNode].hideRules[field].push(compiledRuleNode);
                    } else if (ruleNode.rule.startsWith('property')) {
                        //add property rule
                        if (!compiledRules[objectNode].propertyRules[field]) {
                            compiledRules[objectNode].propertyRules[field] = [];
                        }
                        compiledRules[objectNode].propertyRules[field].push(compiledRuleNode);
                    } else {
                        //add validation rule
                        if (!compiledRules[objectNode].validationRules[field]) {
                            compiledRules[objectNode].validationRules[field] = [];
                        }
                        compiledRules[objectNode].validationRules[field].push(compiledRuleNode); //TODO IDEA: strip duplicate rules for a field
                    }
                }
            }
        }

        //console.log(`Compiled Rules V2: ${JSON.stringify(compiledRules)}`);
        return compiledRules;
    }

    private compileRule(objectNode: string, rule: string, compiledRules: CompiledObjectRules, variables: any): string {
        //console.log(`Compile Rule: ${rule}`)
        // switch (scope) {
        //   case 'form':
        let varStartPos = 0;
        let varEndPos = 0;
        let varPrefix = '[$';
        let varSuffix = ']';

        let compiledObjectRules: CompiledObjectNode = compiledRules[objectNode];

        if (rule == null) {
            return rule;
        }

        if (variables) {
            rule = this.insertRuleVariableSyntax(rule, variables);
        }

        do {
            varStartPos = rule.indexOf(varPrefix);
            if (varStartPos >= 0) {
                varEndPos = rule.indexOf(varSuffix, varStartPos);
                if (varEndPos > varStartPos) {
                    let varParam = rule.substr(varStartPos, varEndPos - varStartPos + varSuffix.length); //ex:  [$this.field]  [$this.Veh_Num]   [$Contract.Line_Code]
                    let varName = varParam.substr(varPrefix.length, varParam.length - varPrefix.length - varSuffix.length); // strip off prefix and suffix
                    let dotPos = varName.indexOf('.');
                    let varStateObject = varName.substr(0, dotPos); //ex.  this,  Contract,  Line, Location, Article,   // verify they exist in state???
                    let varField = varName.substr(dotPos + 1); //ex.  field, Veh_Num, Line_Code

                    //console.log(`processing var "${varParam}" or rule "${rule}"  startPos=${varStartPos}  endPos=${varEndPos}`);

                    if (!compiledObjectRules.parms[varName] && varName != 'Self') {
                        compiledObjectRules.parms[varName] = { fieldName: varField, stateObject: varStateObject };
                    }

                    rule = rule.replace(varParam, "parm['" + varName + "']");
                } else {
                    //TODO: IDEA: build a list of compile failures and show in UI
                    console.error(`Error: Invalid Rule Syntax.  No closing tag bracket on field reference.  rule: ${rule}`);
                    varStartPos = -1; //drop out of loop
                }
            }
        } while (varStartPos >= 0);

        return rule;
    }

    // This function inserts that syntax of any objectrule variables referenced in the rule.
    // e.g.  hideIf(![$Var.Leased])  ==> hideIf(!([$This.Leased]=='Y'))
    private insertRuleVariableSyntax(rule: string, variables: any): string {
        let varStartPos = 0;
        let varEndPos = 0;
        let varPrefix = '[$Var.';
        let varSuffix = ']';

        if (rule == null) {
            return rule;
        }

        do {
            varStartPos = rule.indexOf(varPrefix);
            if (varStartPos >= 0) {
                varEndPos = rule.indexOf(varSuffix, varStartPos);

                let varParam = rule.substr(varStartPos, varEndPos - varStartPos + varSuffix.length); //ex:  [$Var.Var_Name]
                let varName = varParam.substr(varPrefix.length, varParam.length - varPrefix.length - varSuffix.length); // strip off prefix and suffix

                if (variables[varName] && variables[varName] > '') {
                    rule = rule.replace(varParam, '(' + variables[varName] + ')');
                } else {
                    console.error(
                        `Undefined variable name specified in rule! rule=${rule}  variable=${varName}   Note that variable names are case senstitive.`
                    );
                    rule = rule.replace(varParam, '(INVALID-VAR-' + varName.toUpperCase() + ')');
                }
            }
        } while (varStartPos >= 0);

        return rule;
    }
}
